import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Loader2,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  RefreshCw,
  Settings,
  History,
  Filter,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTwilioSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [filterClientId, setFilterClientId] = useState('');
  const [filterEventType, setFilterEventType] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, clientsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/settings/twilio-flex`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/clients`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/twilio/events?limit=50`, { headers: getAuthHeaders() })
      ]);
      
      setSettings(settingsRes.data);
      setClients(clientsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load Twilio settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleGlobalEnabled = async () => {
    setSaving(true);
    try {
      const newEnabled = !settings?.global_enabled;
      await axios.put(
        `${API_URL}/api/admin/settings/twilio-flex`,
        { enabled: newEnabled },
        { headers: getAuthHeaders() }
      );
      setSettings({ ...settings, global_enabled: newEnabled });
      toast.success(`Twilio Flex ${newEnabled ? 'enabled' : 'disabled'} globally`);
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const toggleClientEnabled = async (clientId, currentEnabled) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/clients/${clientId}/twilio/toggle`,
        { enabled: !currentEnabled },
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      const updatedConfigs = settings.configs.map(c => 
        c.client_id === clientId ? { ...c, is_active: !currentEnabled } : c
      );
      setSettings({ ...settings, configs: updatedConfigs });
      
      toast.success(`Twilio ${!currentEnabled ? 'enabled' : 'disabled'} for client`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle client');
    }
  };

  const fetchFilteredEvents = async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filterClientId) params.append('client_id', filterClientId);
      if (filterEventType) params.append('event_type', filterEventType);
      
      const response = await axios.get(
        `${API_URL}/api/admin/twilio/events?${params}`,
        { headers: getAuthHeaders() }
      );
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to filter events');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get client name by ID
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-twilio-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6 text-red-500" />
              Twilio Flex Settings
            </h1>
            <p className="text-muted-foreground">Manage Twilio Flex integration for all clients</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Global Toggle Card */}
      <Card className={settings?.global_enabled ? 'border-green-200' : 'border-red-200'}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Global Twilio Flex Setting
          </CardTitle>
          <CardDescription>
            Enable or disable Twilio Flex integration for the entire platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {settings?.global_enabled ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <XCircle className="w-6 h-6 text-red-500" />
              )}
              <div>
                <p className="font-medium">
                  {settings?.global_enabled ? 'Twilio Flex is ENABLED' : 'Twilio Flex is DISABLED'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {settings?.configured_clients || 0} clients configured, {settings?.active_clients || 0} active
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="global-toggle">
                {settings?.global_enabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="global-toggle"
                checked={settings?.global_enabled || false}
                onCheckedChange={toggleGlobalEnabled}
                disabled={saving}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Configured Clients</div>
            <div className="text-2xl font-bold">{settings?.configured_clients || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Active Clients</div>
            <div className="text-2xl font-bold text-green-600">{settings?.active_clients || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Events</div>
            <div className="text-2xl font-bold">{events.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Tasks Created</div>
            <div className="text-2xl font-bold text-purple-600">
              {events.filter(e => e.event_type === 'task_created').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client Configurations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Client Twilio Configurations
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(!settings?.configs || settings.configs.length === 0) ? (
            <div className="text-center py-12 text-muted-foreground">
              <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No clients have configured Twilio yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Account SID</TableHead>
                  <TableHead>Connection</TableHead>
                  <TableHead>Last Test</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.configs.map((config) => (
                  <TableRow key={config.client_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{config.client_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {config.account_sid?.slice(0, 8)}...
                    </TableCell>
                    <TableCell>
                      {config.connection_tested ? (
                        config.last_test_result === 'success' ? (
                          <Badge className="bg-green-100 text-green-800">Connected</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Failed</Badge>
                        )
                      ) : (
                        <Badge variant="outline">Not Tested</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {config.last_test_at ? formatDate(config.last_test_at) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={config.is_active ? 'default' : 'secondary'}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={config.is_active}
                          onCheckedChange={() => toggleClientEnabled(config.client_id, config.is_active)}
                          disabled={!settings.global_enabled}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enable Twilio for Clients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Enable Twilio for Clients</CardTitle>
          <CardDescription>
            Select clients to enable Twilio Flex (they can then configure their own credentials)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {clients.filter(c => !settings?.configs?.find(cfg => cfg.client_id === c.id)).map(client => (
              <div key={client.id} className="flex items-center justify-between p-3 border rounded">
                <span>{client.company_name}</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleClientEnabled(client.id, false)}
                  disabled={!settings?.global_enabled}
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Enable
                </Button>
              </div>
            ))}
            {clients.filter(c => !settings?.configs?.find(cfg => cfg.client_id === c.id)).length === 0 && (
              <p className="col-span-3 text-center text-muted-foreground py-4">
                All clients have been enabled for Twilio
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Events Log */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4" />
              Twilio Events Log
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={filterClientId || "all"} onValueChange={(v) => setFilterClientId(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterEventType || "all"} onValueChange={(v) => setFilterEventType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="task_created">Task Created</SelectItem>
                  <SelectItem value="studio_flow_triggered">Studio Flow</SelectItem>
                  <SelectItem value="outbound_call">Outbound Call</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={fetchFilteredEvents}>
                <Filter className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No events recorded yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.slice(0, 20).map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="text-sm">{formatDate(event.timestamp)}</TableCell>
                    <TableCell>{getClientName(event.client_id)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{event.event_type.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {event.details?.task_sid && `Task: ${event.details.task_sid.slice(-8)}`}
                      {event.details?.call_sid && `Call: ${event.details.call_sid.slice(-8)}`}
                      {event.details?.lead_id && ` | Lead: ${event.details.lead_id}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
